import _ from 'lodash';
import React from 'react';
import type { SelectEntityOptionProps } from '../components/SelectEntityOption';
import SelectEntityOption from '../components/SelectEntityOption';
import LocalSearchHighlight from '../search/LocalSearchHighlight';
import {
  PostMatchKey,
  unflattenRefIndex,
} from '../search/entitySearch/localEntitySearch';
import type { FuzzySearchMatch } from '../utils/search/fuzzySearch';
import PostBreadcrumbs from './PostBreadcrumbs';
import PostDetailPreviewPopOver from './PostDetailPreviewPopOver';
import PostIcon from './PostIcon';
import PostTitleWithIcons from './PostTitleWithIcons';
import type { SlimPostWithTopics } from './useAllSlimPosts';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import omitNonTopics from '../utils/omitNonTopics';

function getMostFrequentTopicIndex(
  topicIndexes: (number | undefined)[],
): number {
  const frequencyMap = new Map<number, number>();
  let max = 0;
  let maxElement = 0;

  topicIndexes.forEach((topicIndex) => {
    if (typeof topicIndex !== 'number') return;

    const frequency = (frequencyMap.get(topicIndex) || 0) + 1;
    frequencyMap.set(topicIndex, frequency);

    if (frequency > max) {
      max = frequency;
      maxElement = topicIndex;
    }
  });

  return maxElement;
}

const POST_SELECT_OPTION_POST_FRAGMENT = gql(`
  fragment PostSelectOption_post on Post {
    id
    title
    topics {
      ...PostBreadcrumbs_topic
    }
    ...PostIcon_post
    ...PostTitleWithIcons_post
  }
`);

type MatchWithTopicIndex = FuzzySearchMatch & {
  topicIndex?: number;
};

interface PostSelectOptionProps extends Omit<SelectEntityOptionProps, 'label'> {
  matches: readonly FuzzySearchMatch[];
  post:
    | SlimPostWithTopics
    | DocumentType<typeof POST_SELECT_OPTION_POST_FRAGMENT>;
  focused?: boolean;
  disablePreview?: boolean;
}

const PostSelectOption = React.forwardRef<
  HTMLDivElement,
  PostSelectOptionProps
>(
  (
    {
      matches,
      post,
      focused = false,
      disablePreview = false,
      ...props
    }: PostSelectOptionProps,
    ref,
  ) => {
    const topics = omitNonTopics(post.topics);
    const matchesWithTopicIndex = topics
      ? matches.map<MatchWithTopicIndex>((match) => {
          if (typeof match.refIndex === 'number') {
            switch (match.key) {
              case PostMatchKey.TopicNames:
                return { ...match, topicIndex: match.refIndex };
              case PostMatchKey.AncestorTopicNames: {
                const { innerIndex, outerIndex } = unflattenRefIndex(
                  topics,
                  'ancestors',
                  match.refIndex,
                );
                return {
                  ...match,
                  topicIndex: outerIndex,
                  refIndex: innerIndex,
                };
              }
              default:
                break;
            }
          }

          return match;
        })
      : null;

    const bestTopicIndex = getMostFrequentTopicIndex(
      _.map(matchesWithTopicIndex, 'topicIndex'),
    );

    const breadcrumbsTopic = topics?.[bestTopicIndex];

    const content = (
      <SelectEntityOption
        {...props}
        icon={<PostIcon post={post} bordered={!!post.archivedAt && focused} />}
        secondaryTop={
          breadcrumbsTopic ? (
            <PostBreadcrumbs
              topic={breadcrumbsTopic}
              names={[
                <LocalSearchHighlight
                  key="topicNames"
                  matchKey={PostMatchKey.TopicNames}
                  refIndex={bestTopicIndex}
                  matches={matches}
                  defaultValue={breadcrumbsTopic.name}
                />,
                ...(breadcrumbsTopic.ancestors || []).map((ancestor, index) => (
                  <LocalSearchHighlight
                    key={ancestor.id}
                    matchKey={PostMatchKey.AncestorTopicNames}
                    refIndex={index}
                    matches={matchesWithTopicIndex?.filter(
                      (match) => match.topicIndex === bestTopicIndex,
                    )}
                    defaultValue={ancestor.name}
                  />
                )),
              ]}
            />
          ) : null
        }
        label={
          <PostTitleWithIcons
            title={
              <LocalSearchHighlight
                matches={matches}
                matchKey={PostMatchKey.Title}
                defaultValue={post.title}
              />
            }
            post={post}
            iconSize="small"
          />
        }
        ref={ref}
      />
    );

    return (
      <PostDetailPreviewPopOver
        postId={post.id}
        controls={{
          open: !disablePreview && focused,
          initialDelay: 500,
          loadedDelay: 100,
        }}
      >
        {content}
      </PostDetailPreviewPopOver>
    );
  },
);

export default PostSelectOption;
